import CvVideoStatus from '@cv-videos/models/cv-video-status';
import {
  CvVideoInfoStyled,
  CvVideoOptionStyled,
  CvVideoStatuBarStyled,
} from './cv-video-status-bar.styled';
import { CvVideoStatusChip } from '@atoms/CvVideoStatusChip';
import { useTranslation } from 'react-i18next';
import { Box, Radio, Stack, Typography, useTheme } from '@mui/material';
import { JButton } from '@atoms/JButton';
import useJDialog from '@/hooks/useJDialog';
import { CvVideo } from '@candidates/models/dto/get-one-candidate.dto';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { IconSize } from '@constants';
import { useCallback, useState } from 'react';
import { DividerWithMarginStyled } from '@styled/divider-with-margin.styled';
import { VideoPlayerCard } from '@atoms/VideoPlayerCard';

interface CvVideoStatuBarProps {
  cvVideoStatus: CvVideoStatus;
  selectedId: string;
  title: string;
  cvVideos: CvVideo[];
  onSelect: (id: string) => void;
}

export const CvVideoStatuBar = ({
  cvVideoStatus,
  title,
  cvVideos,
  onSelect,
  selectedId,
}: CvVideoStatuBarProps) => {
  const { t } = useTranslation();
  const { showJDialog } = useJDialog();
  const openChangeCvVideoDialog = () => {
    showJDialog(
      t('CHANGE_CV'),
      <ChangeCvVideoDialog
        selectedId={selectedId}
        cvVideos={cvVideos}
        onSelect={onSelect}
      />,
      'filter',
    );
  };
  return (
    <CvVideoStatuBarStyled>
      <Stack direction="row" gap={2}>
        <Typography data-qa="cv-video-title">{title}</Typography>
        <CvVideoStatusChip
          label={t(`${cvVideoStatus.toUpperCase()}_CV`)}
          status={cvVideoStatus}
        />
      </Stack>

      {cvVideos.length > 1 && (
        <JButton
          onClick={openChangeCvVideoDialog}
          variant="contained"
          size="small"
        >
          {t('CHANGE_CV')}
        </JButton>
      )}
    </CvVideoStatuBarStyled>
  );
};

interface ChangeCvVideoDialogProps {
  cvVideos: CvVideo[];
  onSelect: (id: string) => void;
  selectedId: string;
}

const ChangeCvVideoDialog = ({
  cvVideos,
  onSelect,
  selectedId,
}: ChangeCvVideoDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { closeJDialog } = useJDialog();
  const [checkedId, setCheckedId] = useState<string | undefined>(selectedId);
  const handleConfirm = useCallback(() => {
    if (!checkedId) return;
    onSelect(checkedId);
    closeJDialog();
  }, [setCheckedId, closeJDialog, checkedId]);

  const handleCancel = useCallback(() => {
    closeJDialog();
  }, [closeJDialog]);

  return (
    <Box>
      <Stack px={2} gap={2}>
        {cvVideos.map((item) => (
          <CvVideoOptionStyled
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            key={item.id}
          >
            <Stack gap={1} direction={'row'}>
              <VideoPlayerCard
                data-qa="video-player"
                src={item.videoUrl}
                poster={item.thumbnailUrl}
              />
              <CvVideoInfoStyled gap={1}>
                <Typography variant="body2" data-qa="cv-video-title">
                  {item.name}
                </Typography>
                <CvVideoStatusChip
                  data-qa="cv-video-status"
                  label={t(`${item.status.toUpperCase()}_CV`)}
                  status={item.status}
                ></CvVideoStatusChip>
              </CvVideoInfoStyled>
            </Stack>

            <Radio
              onClick={() => setCheckedId(item.id)}
              checked={checkedId === item.id}
              color="secondary"
              checkedIcon={
                <CheckCircleIcon
                  data-qa={item.status}
                  height={IconSize.md}
                  color={theme.palette.secondary.main}
                />
              }
            />
          </CvVideoOptionStyled>
        ))}
      </Stack>

      <DividerWithMarginStyled />
      <Stack pb={2} px={2} gap={1} direction={'row'}>
        <JButton
          onClick={handleConfirm}
          disabled={!checkedId}
          variant="contained"
          color="info"
        >
          {t('CONFIRM')}
        </JButton>
        <JButton onClick={handleCancel} variant="contained" color="info">
          {t('CANCEL')}
        </JButton>
      </Stack>
    </Box>
  );
};
